import { inject, Injectable } from '@angular/core';
import { ClassLearnType } from '@app/analysis/model/enums/class-learn.enum';
import { NzDrawerRef, NzDrawerService } from 'ng-zorro-antd/drawer';
import { CreateTargetComponent } from '@shared/target/create-target/create-target.component';
import { Subject, Subscription } from 'rxjs';
import { TargetClassForm, TargetCloseEnum } from '@shared/target/target.models';
import { ClassroomType } from '@shared/model/classroom.models';
import { SharedService } from '@shared/service/shared.service';
import { ClassroomService } from '@shared/service/classroom.service';

/**
 * 靶向班服务
 */
@Injectable()
export class TargetService {
  // 打开的抽屉对象
  instance: NzDrawerRef;

  // 创建靶向班返回值监听
  submitSubject: Subject<TargetClassForm> = new Subject();

  subscription: Subscription = null;

  // 关闭事件
  closeSubscription: Subscription = null;

  targetClassComponent: CreateTargetComponent;

  // 创建靶向班传进来的数据
  paramsData = null;

  private drawerSrv = inject(NzDrawerService);
  public sharedSrv = inject(SharedService);
  private classSrv = inject(ClassroomService);

  /**
   * 创建靶向班
   * @param option
   */
  createTargetClass(option: {
    gradeId: number;
    name: string;
    users: Array<{ userId: number; firstName: string }>;
    description?: string;
    classroomId?: number;
    subjectId?: number;
    type?: ClassLearnType;
    taskId?: number;
    examId?: number;
    onOk?: Function;
    onCancel?: Function;
    onSubmit?: Function;
  }) {
    this.paramsData = option;
    this.instance = this.drawerSrv.create<CreateTargetComponent>({
      nzTitle: null,
      nzClosable: false,
      nzContent: CreateTargetComponent,
      nzWidth: '500px',
      // nzKeyboard: false,
      nzContentParams: {
        targetClassName: option.name,
        targetClassDescription: option.description,
        users: option.users,
        selectClassroomId: option.classroomId,
      },
      nzBodyStyle: {
        padding: '0',
      },
    });

    this.instance.afterOpen.subscribe(() => {
      // 获取创建靶向班组件
      this.targetClassComponent = this.instance.getContentComponent();

      this.subscription = this.submitSubject.subscribe(async (params: TargetClassForm) => {
        this.targetClassComponent.isSubmitting = true;
        try {
          await option.onSubmit(params as TargetClassForm);
          this.targetClassComponent.isSubmitting = false;
          this.targetClassComponent.isComplete = true;
          this.targetClassComponent.cdr.detectChanges();
        } finally {
          this.targetClassComponent.isSubmitting = false;
          this.targetClassComponent.cdr.detectChanges();
        }
      });
    });

    // 弹窗关闭事件监听
    this.instance.afterClose.subscribe(async (closeEnum: TargetCloseEnum) => {
      if (closeEnum === undefined || closeEnum === TargetCloseEnum.CLOSE) {
        if (option.onCancel) await option.onCancel(this.targetClassComponent.users);
      }
      if (closeEnum === TargetCloseEnum.FINISH) {
        if (option.onOk) await option.onOk(this.targetClassComponent.users);
      }
      this.subscription.unsubscribe();
    });
  }

  /**
   * 创建靶向班并给出下一步路径
   * @param gradeId
   * @param name
   * @param description
   * @param selectedStudent
   * @param autoDestroy 发布任务后解散
   * @param enableTeacherAutoJoin
   * @param classroomId
   * @param subjectId
   * @param taskId
   * @param examId
   */
  public async doCreateTargetClass(
    gradeId: number,
    name: string,
    description: string,
    selectedStudent: number[],
    autoDestroy: boolean,
    enableTeacherAutoJoin: boolean,
    classroomId?: number,
    subjectId?: number,
    taskId?: number,
    examId?: number
  ) {
    const r = await this.classSrv.newClass({
      name: name,
      grade: gradeId,
      type: ClassroomType.Targeted, // 靶向班
      description: description,
      students: Array.from(new Set(selectedStudent)),
      autoDestroy: autoDestroy,
      enableTeacherAutoJoin: enableTeacherAutoJoin,
      classroomId: classroomId ? classroomId : null,
      subjectId: subjectId ? subjectId : null,
      taskId: taskId ? taskId : null,
      examId: examId ? examId : null,
    });

    this.targetClassComponent.selectClassroomId = r.id;
  }

  async submitTargetClass(type: ClassLearnType, params: TargetClassForm) {
    if (params.classroomId) {
      await this.classSrv.addStudent({
        classroomId: params.classroomId,
        userId: params.userIds.join(','),
      });
    } else {
      await this.switchClassLearnType(ClassLearnType.Task, params);
    }
  }

  async switchClassLearnType(type: ClassLearnType, params: TargetClassForm) {
    switch (type) {
      case ClassLearnType.Grade:
        await this.doCreateTargetClass(
          this.paramsData.gradeId,
          params.targetClassName,
          params.targetClassDescription,
          params.userIds,
          params.autoDestroy,
          params.enableTeacherAutoJoin
        );
        break;
      case ClassLearnType.Discipline:
        await this.doCreateTargetClass(
          this.paramsData.gradeId,
          params.targetClassName,
          params.targetClassDescription,
          params.userIds,
          params.autoDestroy,
          params.enableTeacherAutoJoin,
          null,
          this.paramsData.subjectId,
          null
        );
        break;
      case ClassLearnType.Class:
        await this.doCreateTargetClass(
          this.paramsData.gradeId,
          params.targetClassName,
          params.targetClassDescription,
          params.userIds,
          params.autoDestroy,
          params.enableTeacherAutoJoin,
          params.classroomId,
          this.paramsData.subjectId,
          null
        );
        break;
      case ClassLearnType.InClass:
        await this.doCreateTargetClass(
          this.paramsData.gradeId,
          params.targetClassName,
          params.targetClassDescription,
          params.userIds,
          params.autoDestroy,
          params.enableTeacherAutoJoin,
          params.classroomId,
          this.paramsData.subjectId,
          null
        );
        break;
      case ClassLearnType.Task:
        await this.doCreateTargetClass(
          this.paramsData.gradeId,
          params.targetClassName,
          params.targetClassDescription,
          params.userIds,
          params.autoDestroy,
          params.enableTeacherAutoJoin,
          null,
          null,
          this.paramsData.taskId
        );
        break;
      case ClassLearnType.Exam:
        await this.doCreateTargetClass(
          this.paramsData.gradeId,
          params.targetClassName,
          params.targetClassDescription,
          params.userIds,
          params.autoDestroy,
          params.enableTeacherAutoJoin,
          params.classroomId ? params.classroomId : null,
          this.paramsData.subjectId ? this.paramsData.subjectId : null,
          null,
          this.paramsData.examId
        );
        break;
    }
  }
}
